/**
 * Table style choices: a built-in look (preset) plus the Customise drawer's
 * tweaks. Saved in an Excel report (report default + per-tab override) and,
 * per user, for the Query Console. `resolveTableStyle`
 * (shared/ui/Table/style/looks) turns a choice into a `TableStyle`.
 *
 * `undefined` means Classic grid for the report default and the console,
 * and "inherit the report" for a report tab, so reports saved before this
 * feature load unchanged.
 */

/** The built-in looks, in picker order (`LOOK_PRESETS` holds their tweaks). */
export const PRESET_IDS = ["classic", "navy", "minimal", "light"] as const
/** A built-in look. */
export type PresetId = (typeof PRESET_IDS)[number]

/** How cell borders are drawn: every cell, row bands, booktabs rules or none. */
export type LinesMode = "grid" | "rows" | "rules" | "none"
/** Every lines mode, in the drawer's order. */
export const LINES_MODES: readonly LinesMode[] = ["grid", "rows", "rules", "none"]

/**
 * A font offered in the drawer. `css` is the stack used on screen; its first
 * family is the font Excel gets ("inherit" is written as Calibri).
 */
export interface FontOption {
  id: string
  label: string
  css: string
}

/** The fonts the drawer offers; "app" keeps the page font on screen. */
export const FONT_OPTIONS: readonly FontOption[] = [
  { id: "app", label: "App font", css: "inherit" },
  { id: "calibri", label: "Calibri", css: "Calibri, 'Segoe UI', Arial, sans-serif" },
  { id: "aptos", label: "Aptos", css: "Aptos, Calibri, 'Segoe UI', sans-serif" },
  { id: "arial", label: "Arial", css: "Arial, Helvetica, sans-serif" },
  { id: "segoe", label: "Segoe UI", css: "'Segoe UI', Arial, sans-serif" },
  { id: "verdana", label: "Verdana", css: "Verdana, Geneva, sans-serif" },
  { id: "tahoma", label: "Tahoma", css: "Tahoma, Verdana, sans-serif" },
  { id: "georgia", label: "Georgia", css: "Georgia, 'Times New Roman', serif" },
  { id: "times", label: "Times New Roman", css: "'Times New Roman', Times, serif" },
]

/**
 * One field per drawer control, all optional. Colours are "#rrggbb"; a
 * `null` fill means no fill, a `null` zebra or Total shade means off.
 */
export interface StyleTweaks {
  /** A `FONT_OPTIONS` id. */
  fontFamily?: string
  /** 6 to 36, in steps of 0.5. */
  fontSizePt?: number
  textColor?: string
  bannerFill?: string | null
  bannerText?: string
  subBannerFill?: string | null
  subBannerText?: string
  rowLabelFill?: string | null
  rowLabelText?: string
  rowLabelAlign?: "left" | "center"
  lines?: LinesMode
  lineColor?: string
  zebra?: string | null
  totalShade?: string | null
  sigFill1?: string
  sigFill2?: string
  arrowUpColor?: string
  arrowDownColor?: string
  /** 0 to 4. */
  percentDecimals?: number
  /** 0 to 4. */
  meanDecimals?: number
  /** Excel column widths in characters, 4 to 100. */
  excelStubWidth?: number
  excelDataWidth?: number
  freezePanes?: boolean
}

/** Shape version of the saved tweaks; tweaks of another version are dropped on load. */
export const TWEAKS_VERSION = 1
/** Tweaks as saved: versioned so a later shape can be told apart. */
export type SavedTweaks = StyleTweaks & { v: typeof TWEAKS_VERSION }

/** A saved table style: a built-in look plus the user's tweaks on top of it. */
export interface TableStyleChoice {
  preset: PresetId
  /** Absent: the look as it comes. */
  tweaks?: SavedTweaks
}

/** Classic grid, no tweaks: the look when nothing is saved. */
export const DEFAULT_TABLE_STYLE_CHOICE: TableStyleChoice = { preset: "classic" }

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)

const isPresetId = (v: unknown): v is PresetId =>
  typeof v === "string" && (PRESET_IDS as readonly string[]).includes(v)

type Check = (v: unknown) => boolean

const HEX = /^#[0-9a-f]{6}$/
const hex: Check = (v) => typeof v === "string" && HEX.test(v)
const hexOrNull: Check = (v) => v === null || hex(v)
const numberIn =
  (min: number, max: number, step: number): Check =>
  (v) =>
    typeof v === "number" &&
    Number.isFinite(v) &&
    v >= min &&
    v <= max &&
    Number.isInteger(v / step)
const oneOf =
  (values: readonly unknown[]): Check =>
  (v) =>
    values.includes(v)

const TWEAK_CHECKS: Record<keyof StyleTweaks, Check> = {
  fontFamily: oneOf(FONT_OPTIONS.map((font) => font.id)),
  fontSizePt: numberIn(6, 36, 0.5),
  textColor: hex,
  bannerFill: hexOrNull,
  bannerText: hex,
  subBannerFill: hexOrNull,
  subBannerText: hex,
  rowLabelFill: hexOrNull,
  rowLabelText: hex,
  rowLabelAlign: oneOf(["left", "center"]),
  lines: oneOf(LINES_MODES),
  lineColor: hex,
  zebra: hexOrNull,
  totalShade: hexOrNull,
  sigFill1: hex,
  sigFill2: hex,
  arrowUpColor: hex,
  arrowDownColor: hex,
  percentDecimals: numberIn(0, 4, 1),
  meanDecimals: numberIn(0, 4, 1),
  excelStubWidth: numberIn(4, 100, 1),
  excelDataWidth: numberIn(4, 100, 1),
  freezePanes: (v) => typeof v === "boolean",
}

/**
 * A choice read from storage, made safe:
 * - not an object or no known `preset`: undefined (the default / inherit);
 * - tweaks of another version: dropped, the preset kept;
 * - each tweak kept only when it is a known key with a valid value;
 * - tweaks left with nothing: removed.
 * Never throws.
 */
export const normalizeTableStyleChoice = (
  raw: unknown
): TableStyleChoice | undefined => {
  if (!isRecord(raw) || !isPresetId(raw.preset)) return undefined
  const choice: TableStyleChoice = { preset: raw.preset }
  const tweaks = raw.tweaks
  if (isRecord(tweaks) && tweaks.v === TWEAKS_VERSION) {
    const kept: Record<string, unknown> = {}
    ;(Object.keys(TWEAK_CHECKS) as (keyof StyleTweaks)[]).forEach((key) => {
      if (key in tweaks && TWEAK_CHECKS[key](tweaks[key])) kept[key] = tweaks[key]
    })
    if (Object.keys(kept).length > 0) {
      choice.tweaks = { v: TWEAKS_VERSION, ...(kept as StyleTweaks) }
    }
  }
  return choice
}

/**
 * A copy of `obj` (a report tab, the report section) with `tableStyle`
 * replaced by the normalized choice; anything that is not a valid choice
 * removes the key, so objects saved before the feature keep their shape.
 */
export const withTableStyle = <T extends { tableStyle?: TableStyleChoice }>(
  obj: T,
  raw: unknown
): T => {
  const next: { tableStyle?: TableStyleChoice } = { ...obj }
  const normalized = normalizeTableStyleChoice(raw)
  if (normalized) next.tableStyle = normalized
  else delete next.tableStyle
  return next as T
}

/** Whether the choice changes anything on top of its preset. */
export const isCustomised = (choice: TableStyleChoice | undefined): boolean =>
  Boolean(choice?.tweaks && Object.keys(choice.tweaks).some((key) => key !== "v"))

/**
 * The choice with one tweak set (or removed, for `undefined`). A choice
 * left with no tweaks has no `tweaks` key.
 */
export const withTweak = <K extends keyof StyleTweaks>(
  choice: TableStyleChoice,
  key: K,
  value: StyleTweaks[K] | undefined
): TableStyleChoice => {
  const next: Partial<SavedTweaks> = { ...choice.tweaks }
  delete next.v
  if (value === undefined) delete next[key]
  else next[key] = value
  return Object.keys(next).length > 0
    ? { preset: choice.preset, tweaks: { ...next, v: TWEAKS_VERSION } }
    : { preset: choice.preset }
}

/** The choice a report tab is shown and exported with. */
export const effectiveTableStyle = (
  tab?: TableStyleChoice,
  report?: TableStyleChoice
): TableStyleChoice => tab ?? report ?? DEFAULT_TABLE_STYLE_CHOICE
